import React, { useState, useEffect, useCallback } from 'react';

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };

function PmsOccupancyForecast({ token, selectedProperty }) {
  const [horizon, setHorizon] = useState('30D');
  const [summary, setSummary] = useState(null);
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expandedDate, setExpandedDate] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [comparingBatch, setComparingBatch] = useState(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const loadResults = useCallback(async (h) => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/ai/occupancy-forecast/results?horizon=${h || horizon}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setSummary(data.summary);
      setForecasts(data.forecasts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, horizon, headers]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const loadHistory = async () => {
    try {
      const res = await fetch('/api/v1/ai/occupancy-forecast/history?limit=10', { headers: headers() });
      const data = await res.json();
      if (res.ok) setHistory(data.batches || []);
    } catch (_) { /* ignore */ }
  };

  const generateForecast = async () => {
    setGenerating(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/ai/occupancy-forecast/generate', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify({ horizon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setMessage(`Forecast generated: ${data.summary?.forecast_count || 0} days, avg occupancy ${data.summary?.avg_occupancy?.toFixed(1)}%`);
      setTimeout(() => setMessage(''), 5000);
      await loadResults(horizon);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const loadComparison = async (batchId) => {
    setComparingBatch(batchId);
    try {
      const res = await fetch(`/api/v1/ai/occupancy-forecast/actual-vs-forecast?batch_id=${batchId}`, { headers: headers() });
      const data = await res.json();
      if (res.ok) setComparison(data);
    } catch (_) { /* ignore */ }
  };

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '1rem', maxWidth: 1000 }}>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Occupancy & Revenue Forecast</h2>
        <p style={{ color: 'var(--theme-text-muted)' }}>Please select a property to view forecasts.</p>
      </div>
    );
  }

  const formatCurrency = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
  const formatPct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '-';

  const getOccColor = (pct) => {
    if (pct == null) return 'var(--theme-text-muted)';
    if (pct >= 70) return 'var(--theme-success)';
    if (pct >= 40) return '#f59e0b';
    return 'var(--theme-danger)';
  };

  const warnings = forecasts.filter(f => f.warning_type);

  return (
    <div style={{ padding: '1rem', maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ marginTop: 0, color: 'var(--theme-primary)', marginBottom: 4 }}>Occupancy & Revenue Forecast</h2>
          <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: 0 }}>
            AI-powered forecasting for staffing, procurement, and revenue planning
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Horizon tabs */}
          {['30D', '60D', '90D'].map(h => (
            <button
              key={h}
              onClick={() => { setHorizon(h); loadResults(h); }}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid var(--theme-border)',
                background: horizon === h ? 'var(--theme-primary)' : '#fff',
                color: horizon === h ? '#fff' : '#334155',
                fontWeight: horizon === h ? 700 : 400, cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              {h.replace('D', ' Days')}
            </button>
          ))}
          <button
            onClick={generateForecast}
            disabled={generating}
            style={{
              padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none',
              background: generating ? 'var(--theme-text-muted)' : 'var(--theme-primary)', color: '#fff',
              fontWeight: 600, cursor: generating ? 'wait' : 'pointer', fontSize: '0.85rem',
            }}
          >
            {generating ? 'Generating...' : 'Generate Forecast'}
          </button>
          <button
            onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
            style={{
              padding: '0.5rem 0.9rem', borderRadius: 6, border: '1px solid var(--theme-border)',
              background: showHistory ? 'var(--theme-border-light)' : '#fff', color: '#334155',
              cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            History
          </button>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginTop: '0.5rem' }}>{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading forecast data...</div>
      ) : !summary ? (
        <div style={sectionStyle}>
          <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--theme-text-muted)' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>No forecast available</div>
            <div style={{ fontSize: '0.85rem' }}>Click "Generate Forecast" to create an AI-powered occupancy & revenue forecast.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
            <SummaryCard label="Avg Occupancy" value={formatPct(summary.avg_occupancy)} color={getOccColor(summary.avg_occupancy)}
              sub={summary.hist_avg_occupancy ? `Last yr: ${formatPct(summary.hist_avg_occupancy)}` : null} />
            <SummaryCard label="Avg ADR" value={formatCurrency(summary.avg_adr)} color="var(--theme-primary)"
              sub={summary.hist_avg_adr ? `Last yr: ${formatCurrency(summary.hist_avg_adr)}` : null} />
            <SummaryCard label="Avg RevPAR" value={formatCurrency(summary.avg_revpar)} color="var(--theme-primary)" />
            <SummaryCard label="Total Room Revenue" value={formatCurrency(summary.total_room_revenue)} color="var(--theme-success)" />
            <SummaryCard label="Low-Demand Days" value={summary.low_demand_days || 0} color={summary.low_demand_days > 5 ? 'var(--theme-danger)' : '#f59e0b'} />
            <SummaryCard label="High-Demand Days" value={summary.high_demand_days || 0} color="var(--theme-success)" />
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div style={sectionStyle}>
              <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#334155' }}>Demand Alerts</div>
              {warnings.map((w, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0',
                  fontSize: '0.85rem', color: w.warning_type === 'LOW_DEMAND' ? 'var(--theme-danger)' : 'var(--theme-success)',
                }}>
                  <span style={{ fontWeight: 700 }}>{w.warning_type === 'LOW_DEMAND' ? '▼' : '▲'}</span>
                  <span style={{ color: '#334155' }}>{w.forecast_date} ({w.day_of_week})</span>
                  <span>{w.warning_message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Occupancy Bar Chart */}
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#334155' }}>Occupancy Forecast — {horizon.replace('D', '-Day')} View</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 160, overflowX: 'auto', paddingBottom: '1.5rem' }}>
              {forecasts.map((f, i) => {
                const pct = Number(f.projected_occupancy || 0);
                const barH = Math.max(4, (pct / 100) * 140);
                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: horizon === '30D' ? 28 : 14, flex: 1 }}
                    title={`${f.forecast_date} (${f.day_of_week}): ${pct.toFixed(1)}% occ, ADR ${formatCurrency(f.projected_adr)}`}>
                    <div style={{ fontSize: '0.6rem', color: 'var(--theme-text-secondary)', marginBottom: 2 }}>
                      {horizon === '30D' ? `${pct.toFixed(0)}%` : ''}
                    </div>
                    <div style={{
                      width: '100%', maxWidth: 24, height: barH, borderRadius: '3px 3px 0 0',
                      background: getOccColor(pct), opacity: f.is_weekend ? 0.7 : 1,
                    }} />
                    <div style={{ fontSize: '0.55rem', color: 'var(--theme-text-muted)', marginTop: 2, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                      {horizon === '30D' ? f.forecast_date?.slice(5) : (i % (horizon === '60D' ? 3 : 5) === 0 ? f.forecast_date?.slice(5) : '')}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginTop: '0.25rem' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--theme-success)', borderRadius: 2, marginRight: 4 }} />≥70%</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#f59e0b', borderRadius: 2, marginRight: 4 }} />40-70%</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--theme-danger)', borderRadius: 2, marginRight: 4 }} />&lt;40%</span>
            </div>
          </div>

          {/* Daily Forecast Table */}
          <div style={{ ...sectionStyle, overflowX: 'auto' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#334155' }}>Daily Forecast Detail</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-border-light)', borderBottom: '2px solid var(--theme-border)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Day</th>
                  <th style={thStyle}>Occupancy</th>
                  <th style={thStyle}>Conf. Band</th>
                  <th style={thStyle}>ADR</th>
                  <th style={thStyle}>RevPAR</th>
                  <th style={thStyle}>Room Revenue</th>
                  <th style={thStyle}>OTB</th>
                  <th style={thStyle}>Alert</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.map((f, i) => {
                  const isExpanded = expandedDate === f.forecast_date;
                  const rtBreakdown = typeof f.by_room_type === 'string' ? JSON.parse(f.by_room_type) : (f.by_room_type || []);
                  return (
                    <React.Fragment key={i}>
                      <tr
                        onClick={() => setExpandedDate(isExpanded ? null : f.forecast_date)}
                        style={{
                          cursor: rtBreakdown.length > 0 ? 'pointer' : 'default',
                          borderBottom: '1px solid var(--theme-border-light)',
                          background: f.is_weekend ? '#fefce8' : (i % 2 === 0 ? '#fff' : '#fafafa'),
                        }}
                      >
                        <td style={tdStyle}>{f.forecast_date}</td>
                        <td style={tdStyle}>
                          {f.day_of_week?.slice(0, 3)}
                          {f.is_weekend && <span style={{ fontSize: '0.7rem', color: '#f59e0b', marginLeft: 4 }}>WE</span>}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: getOccColor(f.projected_occupancy) }}>
                          {formatPct(f.projected_occupancy)}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--theme-text-secondary)', fontSize: '0.78rem' }}>
                          {f.confidence_low != null ? `${formatPct(f.confidence_low)} – ${formatPct(f.confidence_high)}` : '-'}
                          {f.confidence_level && (
                            <span style={{
                              marginLeft: 4, fontSize: '0.65rem', padding: '1px 4px', borderRadius: 3,
                              background: f.confidence_level === 'HIGH' ? '#dcfce7' : f.confidence_level === 'MEDIUM' ? '#fef3c7' : '#fee2e2',
                              color: f.confidence_level === 'HIGH' ? 'var(--theme-success)' : f.confidence_level === 'MEDIUM' ? 'var(--theme-warning)' : 'var(--theme-danger)',
                            }}>{f.confidence_level}</span>
                          )}
                        </td>
                        <td style={tdStyle}>{formatCurrency(f.projected_adr)}</td>
                        <td style={tdStyle}>{formatCurrency(f.projected_revpar)}</td>
                        <td style={tdStyle}>{formatCurrency(f.projected_room_revenue)}</td>
                        <td style={tdStyle}>{f.on_the_books ?? '-'}</td>
                        <td style={tdStyle}>
                          {f.warning_type === 'LOW_DEMAND' && <span style={{ color: 'var(--theme-danger)', fontWeight: 600 }}>▼ Low</span>}
                          {f.warning_type === 'HIGH_DEMAND' && <span style={{ color: 'var(--theme-success)', fontWeight: 600 }}>▲ High</span>}
                        </td>
                      </tr>
                      {isExpanded && rtBreakdown.length > 0 && (
                        <tr>
                          <td colSpan={9} style={{ padding: '0.5rem 1rem', background: 'var(--theme-bg-light)' }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.8rem', color: '#475569' }}>Room Type Breakdown</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--theme-border)' }}>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Room Type</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Occupancy</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>ADR</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rtBreakdown.map((rt, j) => (
                                  <tr key={j} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{rt.room_type}</td>
                                    <td style={{ padding: '0.3rem 0.5rem', color: getOccColor(rt.occupancy_pct) }}>{formatPct(rt.occupancy_pct)}</td>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{formatCurrency(rt.adr)}</td>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{formatCurrency(rt.revenue)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Generated at */}
          {summary?.created_at && (
            <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)', marginTop: '0.5rem', textAlign: 'right' }}>
              Last generated: {new Date(summary.created_at).toLocaleString()} | Provider: {summary.ai_provider} / {summary.ai_model}
            </div>
          )}
        </>
      )}

      {/* History Panel */}
      {showHistory && (
        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#334155' }}>Forecast History</div>
          {history.length === 0 ? (
            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No previous forecasts found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-border-light)', borderBottom: '2px solid var(--theme-border)' }}>
                  <th style={thStyle}>Generated</th>
                  <th style={thStyle}>Horizon</th>
                  <th style={thStyle}>Period</th>
                  <th style={thStyle}>Avg Occ</th>
                  <th style={thStyle}>Avg ADR</th>
                  <th style={thStyle}>Room Revenue</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <React.Fragment key={h.batch_id}>
                    <tr style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                      <td style={tdStyle}>{new Date(h.created_at).toLocaleString()}</td>
                      <td style={tdStyle}>{h.forecast_horizon}</td>
                      <td style={tdStyle}>{h.forecast_start_date} → {h.forecast_end_date}</td>
                      <td style={{ ...tdStyle, color: getOccColor(h.avg_occupancy) }}>{formatPct(h.avg_occupancy)}</td>
                      <td style={tdStyle}>{formatCurrency(h.avg_adr)}</td>
                      <td style={tdStyle}>{formatCurrency(h.total_room_revenue)}</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => loadComparison(h.batch_id)}
                          style={{
                            padding: '0.25rem 0.6rem', borderRadius: 4, border: '1px solid var(--theme-border)',
                            background: '#fff', color: 'var(--theme-primary)', cursor: 'pointer', fontSize: '0.78rem',
                          }}
                        >
                          Compare
                        </button>
                      </td>
                    </tr>
                    {comparingBatch === h.batch_id && comparison && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0.75rem', background: 'var(--theme-bg-light)' }}>
                          <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#475569' }}>
                            Forecast vs Actual — Accuracy: {' '}
                            <span style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem',
                              background: comparison.accuracy?.accuracy_rating === 'HIGH' ? '#dcfce7' : comparison.accuracy?.accuracy_rating === 'MEDIUM' ? '#fef3c7' : '#fee2e2',
                              color: comparison.accuracy?.accuracy_rating === 'HIGH' ? 'var(--theme-success)' : comparison.accuracy?.accuracy_rating === 'MEDIUM' ? 'var(--theme-warning)' : 'var(--theme-danger)',
                            }}>
                              {comparison.accuracy?.accuracy_rating} ({comparison.accuracy?.compared_days} days compared)
                            </span>
                          </div>
                          {comparison.comparisons?.filter(c => c.actual_occupancy != null).length > 0 ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--theme-border)' }}>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Date</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Forecast Occ</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Actual Occ</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Variance</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Forecast ADR</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>Actual ADR</th>
                                  <th style={{ ...thStyle, padding: '0.3rem 0.5rem' }}>ADR Var%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {comparison.comparisons.filter(c => c.actual_occupancy != null).map((c, i) => (
                                  <tr key={i} style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{c.forecast_date}</td>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{formatPct(c.projected_occupancy)}</td>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{formatPct(c.actual_occupancy)}</td>
                                    <td style={{ padding: '0.3rem 0.5rem', color: Math.abs(c.occupancy_variance) > 5 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                                      {c.occupancy_variance > 0 ? '+' : ''}{Number(c.occupancy_variance).toFixed(1)}pp
                                    </td>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{formatCurrency(c.projected_adr)}</td>
                                    <td style={{ padding: '0.3rem 0.5rem' }}>{formatCurrency(c.actual_adr)}</td>
                                    <td style={{ padding: '0.3rem 0.5rem', color: Math.abs(c.adr_variance_pct) > 5 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                                      {c.adr_variance_pct > 0 ? '+' : ''}{Number(c.adr_variance_pct || 0).toFixed(1)}%
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ color: 'var(--theme-text-muted)', fontSize: '0.82rem' }}>No actual data available yet for comparison. Night audit data is needed for dates in this forecast.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Helpers ─── */
const thStyle = { padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' };
const tdStyle = { padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' };

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8,
      padding: '0.75rem 1rem', textAlign: 'center',
    }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--theme-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default PmsOccupancyForecast;
